import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface ArticleContentItem {
  type: "heading" | "paragraph" | "list" | "steps" | "checklist" | "categories" | "faq";
  text?: string;
  title?: string;
  items?: (string | { num?: string; title?: string; text?: string; name?: string; desc?: string; icon?: string; q?: string; a?: string })[];
}

interface ArticleLayoutProps {
  title: string;
  description: string;
  readingTime: string;
  tag: string;
  heroImage: string;
  content: ArticleContentItem[];
}

function getItemText(item: string | { text?: string }): string {
  if (typeof item === "string") return item;
  return item.text || "";
}

function isObjectItem(item: string | Record<string, unknown>): item is Record<string, unknown> {
  return typeof item !== "string";
}

export default function ArticleLayout({ title, description, readingTime, tag, heroImage, content }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 md:pt-28">
        <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200 mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Powrót do strony głównej
          </Link>

          <header className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-semibold bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                {tag}
              </span>
              <span className="text-xs text-foreground-400">{readingTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {description}
            </p>
          </header>

          <div className="mb-8 md:mb-10">
            <img
              src={heroImage}
              alt={title}
              className="w-full h-56 md:h-80 object-cover object-center rounded-lg"
            />
          </div>

          <div className="prose-content">
            {content.map((block, idx) => {
              if (block.type === "heading" && block.text) {
                return (
                  <section key={idx} className="mb-8 md:mb-10">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                      {block.text}
                    </h2>
                  </section>
                );
              }

              if (block.type === "paragraph" && block.text) {
                return (
                  <section key={idx} className="mb-8 md:mb-10">
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                      {block.text}
                    </p>
                  </section>
                );
              }

              if (block.type === "list" && block.items) {
                return (
                  <section key={idx} className="mb-8 md:mb-10">
                    {block.title && (
                      <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                        {block.title}
                      </h3>
                    )}
                    <div className="bg-accent-50 border border-accent-200/50 rounded-lg p-5 md:p-6">
                      <ul className="flex flex-col gap-2.5">
                        {block.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm md:text-base text-foreground-700">
                            <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                            <span>{getItemText(item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                );
              }

              if (block.type === "steps" && block.items) {
                return (
                  <section key={idx} className="mb-8 md:mb-10">
                    <div className="flex flex-col gap-5">
                      {block.items.filter(isObjectItem).map((item, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <span className="font-heading text-lg font-bold text-primary-600">
                              {(item as { num?: string }).num}
                            </span>
                          </div>
                          <div>
                            {(item as { title?: string }).title && (
                              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-1">
                                {(item as { title?: string }).title}
                              </h3>
                            )}
                            {(item as { text?: string }).text && (
                              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                                {(item as { text?: string }).text}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              if (block.type === "checklist" && block.items) {
                return (
                  <section key={idx} className="mb-8 md:mb-10">
                    <div className="bg-background-100/70 border border-background-200/70 rounded-lg p-5 md:p-6">
                      <ul className="flex flex-col gap-3">
                        {block.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-700">
                            <i className="ri-shield-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                            <span>{getItemText(item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                );
              }

              if (block.type === "categories" && block.items) {
                return (
                  <section key={idx} className="mb-8 md:mb-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {block.items.filter(isObjectItem).map((item, i) => (
                        <div
                          key={i}
                          className="flex gap-3 p-4 bg-background-100/70 rounded-lg border border-background-200/50"
                        >
                          <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className={`${(item as { icon?: string }).icon} text-accent-600 text-sm`}></i>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-foreground-800 mb-1">
                              {(item as { name?: string }).name}
                            </h4>
                            {(item as { desc?: string }).desc && (
                              <p className="text-xs text-foreground-500 leading-relaxed">
                                {(item as { desc?: string }).desc}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              if (block.type === "faq" && block.items) {
                return (
                  <section key={idx} className="mb-8 md:mb-10">
                    <div className="flex flex-col gap-3">
                      {block.items.filter(isObjectItem).map((item, i) => (
                        <div
                          key={i}
                          className="bg-background-100/70 border border-background-200/70 rounded-lg p-4 md:p-5"
                        >
                          <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                            {(item as { q?: string }).q}
                          </h4>
                          <p className="text-sm text-foreground-600 leading-relaxed">
                            {(item as { a?: string }).a}
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }

              return null;
            })}

            <section className="mb-10 md:mb-14 p-6 md:p-8 bg-primary-50/70 rounded-lg border border-primary-200/50 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                Gotowy, aby zacząć?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-6 max-w-lg mx-auto">
                Przejdź do Kakobuy Spreadsheet i odkryj tysiące sprawdzonych produktów w jednym miejscu.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-7 py-3 text-sm md:text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
                >
                  Kakobuy Spreadsheet
                </a>
                <Link
                  to="/"
                  className="px-7 py-3 text-sm md:text-base font-semibold text-primary-500 border-2 border-primary-300 hover:border-primary-500 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Powrót do strony głównej
                </Link>
              </div>
            </section>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}